/**
 * Laravel's `public` disk is exposed at `/storage/...` (see `public/storage` → `storage/app/public`).
 * A legacy misconfiguration used `/storage/app/public/...`, which is not a web route and returns 404.
 */
function normalizePublicDiskBaseUrl(url) {
  const u = String(url || "").trim();
  if (!u) return u;
  return u.replace(/storage\/app\/public/gi, "storage");
}

// Configurable via .env
export let BASE_URL = process.env.REACT_APP_BASE_URL || "https://backend.rezmaze.com/api";
export const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_KEY || "6LedNs8pAAAAAGRuNp84IbDOogE0rXOu6ts23TLp";
const _storageUrlRaw =
  process.env.REACT_APP_STORAGE_URL || "https://backend.rezmaze.com/storage/";
export const STORAGE_URL = (() => {
  let u = normalizePublicDiskBaseUrl(_storageUrlRaw);
  if (u && !u.endsWith("/")) u += "/";
  return u;
})();

/**
 * PDF viewer route is registered in Laravel `routes/web.php` as `resume-viewer`.
 * The web root is already `public/`, so URLs must not include an extra `/public/` segment.
 */
function normalizeResumeViewerBaseUrl(url) {
  const u = String(url || "").trim();
  if (!u) return u;
  return u.replace(/\/public\/resume-viewer/gi, "/resume-viewer");
}

const _resumeViewerRaw =
  process.env.REACT_APP_RESUME_FILE_PATH ||
  "https://backend.rezmaze.com/resume-viewer";
export const Resume_File_Path = normalizeResumeViewerBaseUrl(_resumeViewerRaw);
// PayPal Configuration
// For testing: Use Sandbox Client ID (works from any country, no real account needed)
// For production: Use Live Client ID
export const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "AQSRigNRvdBQ-b-evXDHOSd7fM5s3h2ajNlwDFHVxyZ0ezJgF7IrzBXqaglxQqnRfzZxprKPmp5uGuSR";
export const PAYPAL_ENVIRONMENT = process.env.REACT_APP_PAYPAL_ENVIRONMENT || "sandbox"; // "sandbox" or "production"

// Auth0: REACT_APP_AUTH0_DOMAIN = tenant hostname from Auth0 Dashboard → Settings → Domain
// (e.g. dev-abc.us.auth0.com). NOT the API Identifier URL — do not use auth.rezmaze.com here
// unless you completed Auth0 Custom Domain + DNS for that host.
// REACT_APP_AUTH0_AUDIENCE = API Identifier (can stay https://auth.rezmaze.com/api).
export const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN || "";
export const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID || "";
export const AUTH0_AUDIENCE =
  process.env.REACT_APP_AUTH0_AUDIENCE || "https://auth.rezmaze.com/api";

/** OAuth return path (must be listed in Auth0 SPA "Allowed Callback URLs"). */
export const AUTH0_CALLBACK_PATH =
  process.env.REACT_APP_AUTH0_CALLBACK_PATH || "/auth-exchange";

/** Full redirect_uri sent to Auth0 — use this everywhere so we skip flashing the home route. */
export function getAuth0RedirectUri() {
  if (typeof window === "undefined") {
    return "";
  }
  const path = AUTH0_CALLBACK_PATH.startsWith("/")
    ? AUTH0_CALLBACK_PATH
    : `/${AUTH0_CALLBACK_PATH}`;
  return `${window.location.origin}${path}`;
}

export const AUTH0_CONFIGURED =
  !!(
    AUTH0_DOMAIN &&
    AUTH0_CLIENT_ID &&
    AUTH0_AUDIENCE
  );

/**
 * Preset entries for `REACT_APP_AUTH_CONNECTION` (comma-separated list).
 * Keys are normalized: lowercased, spaces removed. Unknown tokens are treated as custom Auth0 connection names.
 */
const AUTH0_SOCIAL_PRESETS = {
  google: { connection: "google-oauth2", variant: "google", label: "Continue with Google" },
  "google-oauth2": {
    connection: "google-oauth2",
    variant: "google",
    label: "Continue with Google",
  },
  facebook: { connection: "facebook", variant: "facebook", label: "Continue with Facebook" },
  github: { connection: "github", variant: "github", label: "Continue with GitHub" },
  /**
   * Auth0 connection name `linkedin` (classic). Must be enabled for your SPA in Auth0.
   * For OpenID Connect + reliable email, enable connection `linkedin_oidc` in Auth0 and add
   * `linkedin_oidc` to REACT_APP_AUTH_CONNECTION (preset key below), or use that name alone.
   */
  linkedin: {
    connection: "linkedin",
    variant: "linkedin",
    label: "Continue with LinkedIn",
  },
  linkedin_oidc: {
    connection: "linkedin_oidc",
    variant: "linkedin",
    label: "Continue with LinkedIn",
  },
  /** Alias for `linkedin` — same Auth0 connection `linkedin`. */
  "linkedin-classic": {
    connection: "linkedin",
    variant: "linkedin",
    label: "Continue with LinkedIn",
  },
};

/**
 * Social login buttons for `/login`, from **`REACT_APP_AUTH_CONNECTION`**.
 * Comma- or semicolon-separated, e.g. `google,facebook,github,linkedin`.
 * Omit or leave empty → no social buttons. `false` / `off` / `none` → none.
 *
 * @returns {{ id: string, connection: string, variant: string, label: string }[]}
 */
function parseAuth0SocialLoginButtonsFromEnv() {
  const raw = (process.env.REACT_APP_AUTH_CONNECTION ?? "").trim();
  if (!raw || /^(false|off|none)$/i.test(raw)) {
    return [];
  }
  const parts = raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  const out = [];
  const seen = new Set();
  for (const part of parts) {
    const key = part.toLowerCase().replace(/\s+/g, "");
    const preset = AUTH0_SOCIAL_PRESETS[key];
    if (preset) {
      if (seen.has(preset.connection)) {
        continue;
      }
      seen.add(preset.connection);
      out.push({ ...preset, id: preset.connection });
      continue;
    }
    const connection = part.trim();
    if (!connection) {
      continue;
    }
    const dedupeKey = connection.toLowerCase();
    if (seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);
    const display = connection.replace(/[-_]/g, " ");
    out.push({
      connection,
      variant: "generic",
      label: `Continue with ${display}`,
      id: dedupeKey,
    });
  }
  return out;
}

/** Parsed at module load (CRA injects env at build time). */
export const AUTH0_SOCIAL_LOGIN_BUTTONS = parseAuth0SocialLoginButtonsFromEnv();

/** True when Client ID + audience are set but domain is missing (common misconfiguration). */
export const AUTH0_NEEDS_DOMAIN =
  !!AUTH0_CLIENT_ID && !!AUTH0_AUDIENCE && !AUTH0_DOMAIN;

export const IS_MOBILE_VERSION = true;

export default BASE_URL;